import { useQuery } from "@apollo/client";
import type { TrackPlacement } from "analytics/eventCatalog";
import useUser from "components/common/useUser";
import trackEvent from "components/tracking/trackEvent";
import DownloadAppQrCard from "components/website/components/DownloadAppQrCard";
import Footer from "components/website/components/Footer";
import InlineInstallCard from "components/website/components/InlineInstallCard";
import LanguageSwitcher from "components/website/components/LanguageSwitcher";
import Navigation from "components/website/components/navigation/Navigation";
import { PlanGateModal, usePlanGate } from "components/website/components/planGate";
import { getBlogTranslations } from "components/website/marketing/i18n/pages/blog";
import { displayCountryName } from "components/b2c/explore/exploreCommon";
import { EXPLORE_GUIDE } from "graphql/queries/explore";
import { t as ui } from "i18n/index";
import { buildLocalizedPath, isMarketingLangCode } from "i18n/urlLocale";
import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { slugify } from "utils/urlUtils";
import { allocateArticleRails, tagKeyOf } from "./articleRails";
import CountryRouteMap from "./countryMap/CountryRouteMap";
import { shouldRenderRouteMap } from "./countryMap/data";
import { ROUTE_MAP_ID } from "./countryMap/routeStore";
import styles from "./DiscoverCountryPage.module.scss";
import {
	AIPlanBanner,
	type ArticleCard,
	Budget,
	countryTocIds,
	Culture,
	Destinations,
	Faq,
	Festivals,
	fillUi,
	flagUrl,
	Food,
	ForWho,
	GuideHeadingsContext,
	GuideLangContext,
	GuideUiContext,
	bylineWithoutDate,
	Hero,
	InlineReadMore,
	MobileMap,
	NotToMiss,
	Para,
	Phrases,
	Practicalities,
	RelatedArticles,
	RoutesSection,
	Sidebar,
	StipplS,
	useHeading,
	WaveDownloadCTA,
	WhenToVisit,
} from "./discoverSections";

// ── Page ───────────────────────────────────────────────────────────────────
const DiscoverCountryPage: React.FC<{ countrySlug?: string; initialGuide?: any; lang?: string }> = (props) => {
	const history = useHistory();
	const { i18n } = useTranslation(["dashboard"]);
	const { user } = useUser();
	const slug = props.countrySlug || "";

	// Live guide from the knowledge base, and the only source there is. The hardcoded
	// fallback that used to sit behind this was removed once unready geos began 404ing at
	// `discoverReadinessGate`; a geo with no guide now renders the not-found branch below
	// rather than a thin shell built from prototype copy.
	//
	// `initialGuide` is the SSR payload. Skipping the query when it is present is what makes
	// the server render work at all: the SSR Apollo client is a no-op link that never resolves
	// (ssrApolloClient.ts), so a query here would sit in `loading` forever and the page would
	// render null. On the client it also removes the duplicate fetch on hydration.
	//
	// `props.lang`, not `i18n.language`, and this used to be marked KNOWN BROKEN on purpose.
	// `i18n.language` is `undefined` in the marketing bundle, so the query asked the API for
	// English on every locale: a hard load of /nl/discover/… was Dutch because the SSR loader
	// passes the locale explicitly, a client-side navigation into the same URL was not. Which
	// language you got depended on how you arrived.
	//
	// It was left alone on the grounds that changing which content is FETCHED is a product
	// decision rather than a wiring one. That reading has been overtaken twice. The SSR path
	// already fetches the locale the URL names, so this was never a choice between two
	// behaviours — it was two paths disagreeing. And the caution behind it, that a locale might
	// answer with thin or missing prose, no longer applies: the overlay corpus is down to 37
	// English-fallback strings across all six locales.
	const { data, loading } = useQuery(EXPLORE_GUIDE, { variables: { geo: slug, language: props.lang || "en" }, skip: !!props.initialGuide });
	const c = useMemo(() => props.initialGuide || data?.exploreGuide || null, [props.initialGuide, data]);
	const hasDestinations = Boolean((c?.destinations || []).some((g: any) => g?.items?.length));

	// UI-chrome dictionary (kickers, H2s, table copy) for the section chrome shared with the
	// blog articles — see the `GuideUiContext` doc comment in discoverSections.tsx. Computed
	// above the not-found branch below (not just above the return, where it used to live) so
	// that branch's copy is localized too, rather than the two strings it used to carry
	// unconditionally in English.
	// `props.lang`, not `i18n.language`. `MarketingShell.tsx:69` derives the locale from the
	// router's `location.pathname` — which resolves on the server as well as the client — and
	// passes it to every route component as `lang` (it is what line 70 already feeds the nav and
	// footer translations). `i18n.language` is not that: there is no i18next instance in the
	// marketing bundle at all during SSR — the console says so, `NO_I18NEXT_INSTANCE` — so it
	// resolved to English and `getBlogTranslations` returned the English map. The page still read
	// as translated because the guide prose arrives pre-localised from the SSR loader, so the
	// chrome was the only thing exposed: /it/discover/japan shipped Italian prose under "Weather
	// by month" and "Best time". Verified live on 2026-09-08 before this fix: zero Italian
	// dictionary strings on the page.
	const tr = getBlogTranslations(props.lang || i18n.language || "en");

	// The place name the page SAYS, as opposed to the one it looks things up with. `c.name` is
	// canonical English on purpose — image lookup, search matching and affiliate search all key
	// off it (see features/discover-explore.md) — so it is the wrong string to interpolate into a
	// translated sentence. Six to ten of the fourteen H2s on a translated hub read "Nuestros
	// destinos favoritos en Japan" and "Unsere liebsten Reiseziele in France" because the raw
	// name went straight into `vars` (123yb692vwe).
	//
	// `displayCountryName` resolves the exonym through `Intl.DisplayNames`, which covers every
	// country; `DiscoverDestinationPage` has been doing exactly this for its own H1 and breadcrumb
	// since it shipped. No exonym map to maintain.
	//
	// `props.lang`, not `i18n.language`, for the reason the comment above this one gives: there is
	// no i18next instance in the marketing bundle, so `i18n.language` is undefined here and
	// `displayCountryName` would fall through to English on every locale.
	const displayName = displayCountryName(c?.name || "", props.lang || "en");
	// Same source for article-card hrefs, narrowed rather than defaulted to `i18n.language`:
	// `buildLocalizedPath` is typed against `MARKETING_LANG_CODES`, and `i18n.language` is the
	// value that is wrong during SSR (see above), not just a looser fallback.
	const rawLang = props.lang || "";
	const articleLang = isMarketingLangCode(rawLang) ? rawLang : "en";

	// Nine controls on this page open the same flow, and they all reported the same event with
	// the same props — so "which one actually drives trips" was unanswerable. `placement` is the
	// canonical dimension for exactly that (TRACK_PLACEMENTS in analytics/eventCatalog.ts);
	// callers that pass nothing keep the old behaviour rather than lying about where they were.

	// A country hub renders its intro with no H2 at all today, so the section carrying the page's
	// most-quoted prose has no heading for a keyword to sit in. Both default to empty, so nothing
	// renders until a copy file writes one and the page is byte-identical until then.
	//
	// `dictId="introCountry"`, not the bare "intro" default: `DiscoverDestinationPage` populated
	// `guide.section.intro.*` for its own "<City>: what to expect" H2 (123yb692m5g), and that page
	// shares this one's anchor id — the same reason `WhenToVisit` points its own "when" section at
	// `dictId="whenCountry"` instead of the bare "when" `WhenToGo` owns. Without this, adding that
	// destination-only dictionary entry would have made every one of the 19 live country hubs
	// start rendering an unfilled "{name}: what to expect" H2 the moment a `GuideUiContext` with
	// real translations reached this call site — see `introHeading.test.tsx`'s "renders no heading
	// at all where the page never had one". No `guide.section.introCountry.*` key exists in any
	// locale file, so this still falls through to the empty fallback below and the page stays
	// byte-identical.
	const introHeading = useHeading("intro", "", "", { dictId: "introCountry" });

	// `c.updatedAt` is an ISO `YYYY-MM-DD` off the dossier's own frontmatter, carried through by
	// `build_guide_documents.py` (123yb69326q) — replacing a byline that hardcoded "August 2026"
	// literally, in all seven locales, English included, on every /discover page. Formatted with
	// `toLocaleDateString`, not a dictionary lookup: it localises the month name for free (no
	// `guide.chrome.month.*` key needed here, unlike the weather strip, which has no locale to
	// delegate to), and that is the whole reason this is carried as a raw date rather than
	// pre-rendered in the knowledge base build.
	//
	// `props.lang`, not `i18n.language` — same reasoning as every other locale read on this page
	// (see the long comment above `tr`): there is no i18next instance in the marketing bundle
	// during SSR, so `i18n.language` is undefined and the month would render in English on every
	// non-English hub regardless of what `props.lang` says the URL is.
	//
	// Guarded: `toLocaleDateString` throws on a malformed BCP-47 tag, and a bad locale must
	// degrade to the no-date byline below rather than take the page down with it — the same
	// failure mode as a dossier with no `updated_at` at all (2 of 471 today), on purpose.
	let formattedUpdatedAt: string | undefined;
	if (c?.updatedAt) {
		try {
			const updated = new Date(c.updatedAt);
			if (!Number.isNaN(updated.getTime())) {
				formattedUpdatedAt = updated.toLocaleDateString(props.lang || "en", { year: "numeric", month: "long" });
			}
		} catch {
			formattedUpdatedAt = undefined;
		}
	}

	// `guide.chrome.byline` bakes "Updated {date} · " into one string across all seven locale
	// files rather than exposing the date clause as its own key — so there is no dictionary key
	// for "no date, still byline" to look up. Adding one would mean writing and
	// shipping a translation in six files this session does not own, for a ticket that is
	// explicitly about not inventing what those files say. Instead, the no-date byline is
	// derived from the SAME translated string: every one of the seven `guide.chrome.byline`
	// values is two clauses joined by " · ", with `{date}` always inside the first one
	// (verified against every locale file), so dropping "the clause that mentions {date}" before
	// filling is a structural operation on the string the translators already wrote, not a new
	// piece of copy. A geo whose dossier has never been checked (2 today) gets "Written by the
	// Stippl editors" — no invented date, and no stray leading " · " either. There is no read time:
	// it was a hardcoded 14 on every country, and was dropped rather than computed (123yb694ta8).
	const BYLINE_EN = "Updated {date} · Written by the Stippl editors";
	const bylineTemplate = ui(tr, "guide.chrome.byline", BYLINE_EN);
	const byline = formattedUpdatedAt ? fillUi(bylineTemplate, { date: formattedUpdatedAt }) : bylineWithoutDate(bylineTemplate, BYLINE_EN);

	const planGate = usePlanGate("discover_country", "store");

	// The hubs in COUNTRY_ROUTE_MAP_COUNTRIES swap their route cards for the route map, once the payload carries the route stops
	// (the Redis guide cache can still hold one from before). Every other hub renders exactly what it did.
	// `?routemap=1` shows it in this browser on any hub with stops, for review of the hubs not on the list yet: read after mount, so the server HTML
	// and the hydration pass of a hub off the list stay the route cards.
	const [routeMapPreview, setRouteMapPreview] = useState(false);
	useEffect(() => setRouteMapPreview(new URLSearchParams(window.location.search).get("routemap") === "1"), []);
	const routeMap = shouldRenderRouteMap(c, routeMapPreview);

	const handlePlanClick = (placement?: TrackPlacement, section?: string) => {
		trackEvent("open_create_trip", { path: "discover_country", ...(placement ? { placement } : {}), ...(section ? { section } : {}) }, "discover");
		// A logged-in visitor goes to the app rather than opening the trip builder here.
		//
		// This page is served by the MARKETING bundle, which mounts Apollo, the router and
		// BusinessContext and nothing else. `CreateTripModal` calls `useSelector((state) =>
		// state.auth)` and `useSnackbar()`, so mounting it here meant reading a null Redux context:
		// it threw, nothing in the marketing tree catches a render error, and the whole page
		// unmounted to a white screen. That is 123yb692mta — which was mis-attributed twice, first
		// to a plan-gate change and then reverted for nothing, because the reporter and everyone
		// reproducing it were logged in and never went down the logged-out branch at all.
		//
		// Supplying the missing providers here was the other option. Sending them to the app is the
		// better one (Robin, 2026-09-09): it keeps the trip-builder tree — and the ~950 kB
		// GenericDiscoverModal chunk behind it — out of the bundle every homepage and blog visitor
		// downloads, which is the reason the import was lazy in the first place.
		//
		// A full document load, not `history.push`: /home is an app route, and the marketing router
		// has no entry for it.
		if (user?._id) {
			window.location.assign("/home");
			return;
		}
		// The same gate the homepage uses, not a second answer — `history.push("/register")`
		// lived here, and that is how the two drifted apart. See planGate.tsx.
		planGate.requestPlan();
	};

	if (loading && !c) return null;

	if (!c) {
		return (
			<div id="stippl-discover-country">
				{/* Hubs were the only marketing pages that never passed `langMenu` here — the switcher
				    was never mounted, not hidden or broken (123yb692mt8 / 123yb692n1c). Keep it on
				    every <Navigation> below, this branch included: `type="page"` only rewrites the
				    current path's locale prefix, so it works on a page that resolved to nothing. */}
				<Navigation impact={false} langMenu={<LanguageSwitcher type="page" />} />
				<div className={styles.notFound}>
					<h1 className={styles.notFoundTitle}>{ui(tr, "blog.section.guideNotFoundTitle", "We don't have a guide for this country yet")}</h1>
					<button type="button" onClick={() => history.push("/discover")} className={styles.notFoundButton}>
						{ui(tr, "blog.section.guideNotFoundBack", "Back to Discover")}
					</button>
				</div>
				<Footer />
			</div>
		);
	}

	if (!c) return null;

	// Country-level articles aren't tied to one destination, so this route drops the
	// :destination segment that DiscoverDestinationPage's article cards use.
	//
	// Mirrors the guide URLs — /discover/:country and /discover/:country/:destination — on
	// purpose. The path used to carry a leading continent segment, but there is no
	// /discover/:continent page (only commented-out legacy routes in Routes.tsx), so it was a
	// hierarchy level nothing could navigate to, and one derived from a mutable field: had a
	// country's continent ever been corrected, every article URL beneath it would have moved.
	// `/blog/<slug>`, the one URL space articles live in. The nested
	// `/blog/destinations/...` shape these used to build was removed with its routes
	// (2026-08-25) so new articles share the URL space of the 412 already indexed ones.
	//
	// Locale-prefixed with `articleLang`, not left bare: an un-prefixed `/blog/<slug>` always
	// resolves — but to the English article on every non-English hub, because the guide's
	// English-snapshot `slug` (or the un-prefixed fallback below) doesn't carry a locale of its
	// own. `buildExploreGuide` now resolves each rail entry's `slug` against the visitor's
	// locale (graphql/schemas/explore.ts), so the entry itself is already correct; this only
	// keeps the link on the same side of the locale prefix as the page it's on.
	const buildArticleHref = (article: { slug?: string; title: string }) =>
		buildLocalizedPath(article.slug ? `/blog/${article.slug}` : `/blog/${slugify(article.title)}`, articleLang);

	// The number the backend now returns takes over from the English-snapshot `read` string on a
	// non-English hub — see `localizeArticleRail` in graphql/schemas/explore.ts. A rail entry
	// whose translation didn't resolve has no `readingMinutes` and keeps its (English) `read`.
	const localizedArticles: ArticleCard[] = (c.articles || []).map((a: any) =>
		typeof a.readingMinutes === "number" ? { ...a, read: `${a.readingMinutes} ${ui(tr, "blog.article.minAbbr", "min")}` } : a
	);

	// Give each rail its own picks instead of letting them repeat: "Dive deeper" used to be an
	// unfiltered top three, so it always replayed whatever the tag-filtered rails above it had
	// already shown — a country with one Routes article (Germany) rendered it in "Read more
	// stories", "Dive deeper" and the footer grid, three times over (123yb691zu8).
	const [routesRail, diveDeeperRail, foodWildlifeRail] = allocateArticleRails<ArticleCard>(localizedArticles, [
		{ filter: (a) => tagKeyOf(a) === "Routes" },
		{},
		{ filter: (a) => tagKeyOf(a) === "Food" || tagKeyOf(a) === "Wildlife" },
	]);

	// Per-geo eyebrow/H2 overrides from the knowledge base's copy layer. Provided at the root
	// so every section can opt in by its own id instead of taking a prop it usually ignores.
	// Undefined for a geo with no copy file, which is almost all of them today.
	return (
		<GuideUiContext.Provider value={tr}>
			{/* `articleLang`, not `lang`/`props.lang` directly — same narrowed, SSR-correct value the
			    article hrefs above already use, and the one `RichText`'s markdown-link branch needs
			    to localise an in-body link (123yb692y18). Not `useMarketingLang()` — see the
			    `GuideLangContext` doc comment in discoverSections.tsx. */}
			<GuideLangContext.Provider value={articleLang}>
				<GuideHeadingsContext.Provider value={c.headings}>
					<div id="stippl-discover-country" className={styles.page}>
						{/* Must mirror `discoverGuideHead` in marketingRoutes.tsx exactly. Helmet runs after
					    hydration and overwrites whatever SSR put in the head, so a fallback that differs
					    here would replace the served title in the browser a moment after paint — and the
					    two would only disagree on the pages that have copy, which are the ones that matter. */}
						<Helmet>
							{/* `c.name`, not `displayName`: a title tag is an SEO surface and changing one
							    is a keyword decision, not a wiring one. This fallback only fires when the
							    page file carries no title of its own. */}
							<title>{c.title || `${c.name} travel guide | Stippl`}</title>
							<meta name="description" content={c.metaDescription || c.tagline} />
						</Helmet>

						<Navigation impact={true} langMenu={<LanguageSwitcher type="page" />} />

						<Hero
							name={displayName}
							tagline={c.tagline}
							heroPhoto={c.heroPhoto}
							mapEmbed={c.mapEmbed}
							mapMarkers={c.mapMarkers}
							countryCode={c.countryCode}
							continent={c.continent}
							bestTime={c.bestTime}
							destinations={c.destinations}
							quickFacts={c.quickFacts}
							onPlan={() => handlePlanClick("header_cta", "hero")}
							routeMap={routeMap}
						/>

						{/* Five hubs (Germany, Greece, Morocco, New Zealand, the US) have no destination with a
						    page of its own, so the list arrives empty and the section would render a heading,
						    an "All" tab and nothing under it. They keep their other twelve sections. */}
						{hasDestinations && (
							<div className={styles.destinationsWrap}>
								<Destinations
									name={displayName}
									slug={c.slug}
									destinations={c.destinations}
									onPlan={() => handlePlanClick("list_card", "destinations")}
									routeMap={routeMap}
								/>
							</div>
						)}

						{routeMap ? (
							<CountryRouteMap name={displayName} countryCode={c.countryCode} guide={c} />
						) : (
							<RoutesSection name={displayName} countryCode={c.countryCode} mapEmbed={c.mapEmbed} routes={c.routes} />
						)}

						<div className={styles.contentWrap}>
							<div className={styles.contentGrid}>
								<div>
									<div id="intro" className={styles.introBlock}>
										<div className={styles.introHeader}>
											<span className={`${styles.circleBadge} ${styles.introAvatarBadge}`}>
												<img src={flagUrl(c.countryCode)} alt="" className={styles.coverImg} />
											</span>
											<span className={styles.introWrittenBy}>{byline}</span>
										</div>
										{introHeading.kicker && <span className={styles.sectionHeadingKicker}>{introHeading.kicker}</span>}
										{introHeading.h2 && <h2 className={styles.introHeading}>{introHeading.h2}</h2>}
										<div className={styles.introFacts}>
											{c.intro.map((t) => (
												<Para key={t.slice(0, 24)}>{t}</Para>
											))}
										</div>
									</div>

									<MobileMap guide={{ name: c.name, mapEmbed: c.mapEmbed, mapMarkers: c.mapMarkers, isCountry: true }} />

									{/* work/install-card-mobile.md's A3: after the intro, before the reader is
									    into the guide's sections proper. Mobile/tablet only (CSS-hidden on
									    desktop, see InlineInstallCard.scss). A country hub always resolves a
									    country, so this is never the no-country case. */}
									<InlineInstallCard country={displayName} locale={props.lang || i18n.language || "en"} />

									<ForWho name={displayName} forWho={c.forWho} lessIdealFor={c.lessIdealFor} />
									<WhenToVisit
										name={displayName}
										bestTime={c.bestTime}
										whenToVisit={c.whenToVisit}
										months={c.months}
										seasons={c.seasons}
										planningNotes={c.planningNotes}
										onPlan={() => handlePlanClick("card_cta", "when_to_visit")}
									/>
									<InlineReadMore
										articles={routesRail}
										label={fillUi(ui(tr, "blog.section.railReadMore", "Read more {name} stories"), { name: displayName })}
										buildHref={buildArticleHref}
									/>
									<Festivals specialDays={c.specialDays} />
									<AIPlanBanner name={displayName} onPlan={() => handlePlanClick("inline_banner", "ai_plan_banner")} />
									<NotToMiss name={displayName} notToMiss={c.notToMiss} />
									<InlineReadMore articles={diveDeeperRail} label={ui(tr, "blog.section.railDiveDeeper", "Dive deeper")} buildHref={buildArticleHref} />
									<Food name={displayName} food={c.food} onPlan={() => handlePlanClick("card_cta", "food")} isCountry />
									<InlineReadMore articles={foodWildlifeRail} label={ui(tr, "blog.section.railAlsoLike", "You might also like")} buildHref={buildArticleHref} />
									<Culture culture={c.culture} etiquette={c.etiquette} />
									<Budget name={displayName} budget={c.budget} onPlan={() => handlePlanClick("card_cta", "budget")} isCountry />
									<Practicalities name={displayName} practicalities={c.practicalities} />
									<Phrases phrases={c.phrases} />
									{/* `displayName` inside the synthesised `guide`, not `c.name`: Faq reads `guide.name` for
									    its own heading, so the nested shape slipped past the pass that localised every other
									    section and left "Preguntas frecuentes sobre Japan" as the one English H2 on the page. */}
									<Faq guide={{ name: displayName, faqs: c.faqs }} isCountry />

									<div className={styles.feedbackCard}>
										<span className={`${styles.iconCircle} ${styles.feedbackIcon}`}>
											<StipplS size={18} />
										</span>
										<p className={styles.feedbackText}>
											{ui(
												tr,
												"guide.chrome.editorNote",
												"Every guide is written by the Stippl editors and checked against the trips our travellers actually plan. Spotted something out of date?"
											)}{" "}
											<span className={styles.feedbackHighlight}>{ui(tr, "guide.chrome.letUsKnow", "Let us know")}</span>.
										</p>
									</div>
								</div>

								<Sidebar
									name={displayName}
									mapEmbed={c.mapEmbed}
									mapMarkers={c.mapMarkers}
									isCountry
									countryCode={c.countryCode}
									sections={countryTocIds(c)}
									onPlan={() => handlePlanClick("sidebar_menu", "sidebar")}
								/>
							</div>
						</div>

						<WaveDownloadCTA
							photo={c.heroPhoto}
							// KB alt for the same photo the hero decoration uses; falls back to the localised
							// display name for a guide synced before the KB rewrite (123yb6937nz).
							alt={c.heroPhotoCredit?.alt || displayName}
							country={c.name}
							title={fillUi(ui(tr, "blog.article.ctaTitle", "Plan {country} in Stippl"), { country: displayName })}
							body={fillUi(
								ui(
									tr,
									"blog.article.ctaBodyGuide",
									"Everything in this guide, ready to drop into your {country} route, with planning and bookings in one app."
								),
								{ country: displayName }
							)}
							kicker={ui(tr, "blog.article.ctaKicker", "Ready when you are")}
							scanLabel={ui(tr, "blog.article.scanMe", "Scan me")}
							scanAlt={ui(tr, "blog.article.scanAlt", "Scan to download the Stippl app")}
						/>

						<RelatedArticles
							heading={fillUi(ui(tr, "blog.section.allArticles", "All our {name} articles"), { name: displayName })}
							articles={localizedArticles}
							buildHref={buildArticleHref}
							kicker={ui(tr, "blog.article.relatedKicker", "Read on")}
							readLabel={ui(tr, "blog.article.readSuffix", "read")}
							action={
								<a href="#" onClick={(e) => e.preventDefault()} className={styles.relatedSeeAll}>
									{fillUi(ui(tr, "blog.section.seeAllGuides", "See all {name} guides"), { name: displayName })}
									<svg
										width="15"
										height="15"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2.2"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<path d="M5 12h14M13 6l6 6-6 6" />
									</svg>
								</a>
							}
						/>

						<Footer />
						{/* Floating install card (bugs/blog-install-card-never-renders.md,
						    work/owned-link-install-cta.md): unconditional, including alongside
						    WaveDownloadCTA above — it reveals at ~500px (~5% into the page) while the
						    wave CTA sits near the very end, so gating it off would hand the install
						    affordance to only readers who reach the bottom. Reach beats tidiness; do
						    not re-add a gate. No non-default props: this page scrolls `window` (not an
						    inner container) at every viewport, so the default reveal="scroll" fires;
						    `.stippl-footer .footer-row` exists for the compact-viewport watcher; and
						    the only other fixed/sticky element near the viewport (the TOC sidebar) sits
						    at the right of the content grid, clear of the card's default bottom-left
						    dock — so neither `desktopRight` nor `aboveBottomNav` is needed. Verified via
						    Playwright against production before this was added. */}
						{/* A route-map hub's panel keeps its save button in the card's bottom-left slot: the card steps aside while the map is on screen. */}
						<DownloadAppQrCard {...(routeMap ? { yieldTo: ROUTE_MAP_ID } : {})} />

						<PlanGateModal
							gate={planGate}
							title={ui(tr, "guide.auth.country", "Create a free account to plan your trip")}
							loginTitle={ui(tr, "guide.auth.login", "Log in to plan with AI")}
						/>
					</div>
				</GuideHeadingsContext.Provider>
			</GuideLangContext.Provider>
		</GuideUiContext.Provider>
	);
};

export default DiscoverCountryPage;
